import { positionFloatingElement } from '@/src/dom/floating_position.js'

const HIDE_DELAY_MS = 150

function normalize(value) {
  if (!value) return null
  if (typeof value === 'string') return { text: value, placement: 'top' }
  if (!value.text) return null
  return { text: value.text, placement: value.placement || 'top' }
}

// Icon-only triggers have no accessible name of their own, so mirror it
// into aria-label. Unless a better text name exists.
function syncAriaLabel(el) {
  const state = el.__tooltip
  const config = normalize(state.value)
  const hasOwnName = el.textContent.trim() !== '' || el.hasAttribute('aria-labelledby') ||
    (!state.ownsAriaLabel && el.hasAttribute('aria-label'))

  if (config && !hasOwnName) {
    el.setAttribute('aria-label', config.text)
    state.ownsAriaLabel = true
  } else if (state.ownsAriaLabel) {
    el.removeAttribute('aria-label')
    state.ownsAriaLabel = false
  }
}

function show(el) {
  const state = el.__tooltip
  const config = normalize(state?.value)
  if (!config) return
  clearTimeout(state.hideTimer)

  if (!state.tipEl) {
    const tip = document.createElement('div')
    tip.setAttribute('role', 'tooltip')
    tip.innerHTML = '<div class="tooltip-arrow"></div><div class="tooltip-inner"></div>'
    document.body.appendChild(tip)
    state.tipEl = tip
  }

  state.tipEl.className = `tooltip fade ${config.placement}`
  state.tipEl.querySelector('.tooltip-inner').textContent = config.text
  state.tipEl.style.display = 'block'
  positionFloatingElement(el, state.tipEl, config.placement)

  requestAnimationFrame(() => state.tipEl && state.tipEl.classList.add('in'))
}

function hide(el) {
  const state = el.__tooltip
  if (!state || !state.tipEl) return
  state.tipEl.classList.remove('in')
  state.hideTimer = setTimeout(() => {
    state.tipEl?.remove()
    state.tipEl = null
  }, HIDE_DELAY_MS)
}

export function hideTooltip(el) {
  if (el) hide(el)
}

export default {
  mounted(el, binding) {
    el.__tooltip = { value: binding.value, tipEl: null, hideTimer: null, ownsAriaLabel: false }
    syncAriaLabel(el)
    el.__tooltipHandlers = { show: () => show(el), hide: () => hide(el) }
    el.addEventListener('mouseenter', el.__tooltipHandlers.show)
    el.addEventListener('mouseleave', el.__tooltipHandlers.hide)
    el.addEventListener('focus', el.__tooltipHandlers.show)
    el.addEventListener('blur', el.__tooltipHandlers.hide)
  },
  updated(el, binding) {
    if (!el.__tooltip) return
    el.__tooltip.value = binding.value
    syncAriaLabel(el)
  },
  unmounted(el) {
    // Do not route through hide()'s delayed setTimeout here: cancelling
    // that same timer immediately below would leak the tooltip node
    // forever if the tooltip was visible at unmount time. Remove it
    // synchronously instead — no fade is needed on teardown.
    clearTimeout(el.__tooltip?.hideTimer)
    el.__tooltip?.tipEl?.remove()
    el.removeEventListener('mouseenter', el.__tooltipHandlers.show)
    el.removeEventListener('mouseleave', el.__tooltipHandlers.hide)
    el.removeEventListener('focus', el.__tooltipHandlers.show)
    el.removeEventListener('blur', el.__tooltipHandlers.hide)
    delete el.__tooltip
    delete el.__tooltipHandlers
  }
}
